<template>
  <div class="contacts-upload-linkedin p-5">
    <template v-if="readyToUpload === false">
      <b-card-group deck>

        <b-card class="border-0" :title="$t('onboarding.sync-connections.upload-linkedin.step1-title') | prefix(1)"
          header-class="bg-white border-0" img-top img-src="/static/images/illustrations/3.svg">
            <i18n path="onboarding.sync-connections.upload-linkedin.step1-description" tag="p">
              <template v-slot:request-archive-cta>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.request-archive-cta') }}</strong>
              </template>
              <template v-slot:step1-li-heading>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.step1-li-heading') }}</strong>
              </template>
            </i18n>
        </b-card>

        <b-card class="border-0" :title="$t('onboarding.sync-connections.upload-linkedin.step2-title') | prefix(2)"
          header-class="bg-white border-0">
            <template v-slot:header>
              <b-img class="mb-2 w-100" src="/static/images/screenshots/LinkedIn-2021-06-14-1113.png"
                title="Click to preview" v-b-modal.request-archive-screen/>
              <b-modal id="request-archive-screen" title="Request Your Archive" size="lg" hide-footer>
                <b-img class="w-100" src="/static/images/screenshots/LinkedIn-2021-06-14-1113.png"/>
              </b-modal>
            </template>
            <i18n path="onboarding.sync-connections.upload-linkedin.step2-description" tag="p">
              <template v-slot:step2-li-heading>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.step2-li-heading') }}</strong>
              </template>
              <template v-slot:step2-li-option>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.step2-li-option') }}</strong>
              </template>
              <template v-slot:step2-li-button>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.step2-li-button') }}</strong>
              </template>
            </i18n>
            <i18n path="onboarding.sync-connections.upload-linkedin.step2-video-prompt" tag="p" class="m-0">
              <template v-slot:step2-video-button>
                <a href="#" @click.prevent="() => false" v-b-modal.video-instructions>
                  {{ $t('onboarding.sync-connections.upload-linkedin.step2-video-button') }}
                </a>
              </template>
            </i18n>
            <b-modal id="video-instructions" :title="$t('onboarding.sync-connections.upload-linkedin.step2-video-title')"
              size="lg" hide-footer>
                <video-player :options="videoOptions"/>
            </b-modal>
        </b-card>

        <b-card class="border-0" :title="$t('onboarding.sync-connections.upload-linkedin.step3-title') | prefix(3)"
          header-class="bg-white border-0" img-top img-src="/static/images/illustrations/4.svg">
            <p>{{ $t('onboarding.sync-connections.upload-linkedin.step3-description') }}</p>
        </b-card>

      </b-card-group>

      <b-alert class="d-block d-lg-none my-3" show variant="warning">
        <strong>{{ $t('onboarding.sync-connections.upload-linkedin.cannot-sync-mobile-title') }}</strong>
        {{ $t('onboarding.sync-connections.upload-linkedin.cannot-sync-mobile-description') }}
      </b-alert>

      <div class="action-buttons">
        <b-btn class="my-3" size="lg" :href="linkedInUrl" target="_blank" @click="openLinkedInDelayModal" variant="linkedin">
          {{ $t('onboarding.sync-connections.upload-linkedin.request-archive-cta') }}
        </b-btn>
        <b-btn class="m-0 text-muted" size="sm" variant="link" @click="() => readyToUpload = !readyToUpload">
          {{ $t('onboarding.sync-connections.upload-linkedin.ready-to-upload-cta') }}
        </b-btn>
      </div>
    </template>
    <template v-else>
      <b-card-group>

        <b-card class="border-0"/>

        <b-card class="border-0" :title="$t('onboarding.sync-connections.upload-linkedin.step4-title') | prefix(3)"
          header-class="bg-white border-0">
            <template v-slot:header>
              <b-img class="mb-2 w-100" src="/static/images/screenshots/LinkedIn-2021-06-14-1114.png"
                v-b-modal.request-archive-screen/>
              <b-modal id="request-archive-screen" :title="$t('onboarding.sync-connections.upload-linkedin.request-archive-cta')"
                size="lg" hide-footer>
                  <b-img class="w-100" src="/static/images/screenshots/LinkedIn-2021-06-14-1114.png"/>
              </b-modal>
            </template>
            <i18n path="onboarding.sync-connections.upload-linkedin.step4-description" tag="p">
              <template v-slot:step4-li-button>
                <strong><a :href="linkedInUrl" target="_blank">{{ $t('onboarding.sync-connections.upload-linkedin.step4-li-button') }}</a></strong>
              </template>
              <template v-slot:step4-li-download-cta>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.step4-li-download-cta') }}</strong>
              </template>
              <template v-slot:upload-archive-cta>
                <strong>{{ $t('onboarding.sync-connections.upload-linkedin.upload-archive-cta') }}</strong>
              </template>
            </i18n>
        </b-card>

        <b-card class="border-0"/>

      </b-card-group>
      <div class="action-buttons">
        <b-btn class="my-3" size="lg" variant="linkedin" @click="askForFile">
          {{ $t('onboarding.sync-connections.upload-linkedin.upload-archive-cta') }}
        </b-btn>
        <b-btn class="m-0 text-muted" size="sm" variant="link" @click="() => readyToUpload = !readyToUpload">
          {{ $t('onboarding.sync-connections.upload-linkedin.needs-request-archive-cta') }}
        </b-btn>
      </div>
    </template>

    <!-- <input class="d-none" type="file" ref="fileInput" accept=".csv,.zip" @change="e => receivedFile(e)"/> -->

    <b-modal id="linkedin-redirected" :title="isOnboarding ? $t('onboarding.sync-connections.upload-linkedin.redirected-modal-title') : undefined"
      hide-footer no-close-on-backdrop body-class="text-center mx-5 px-5" footer-class="justify-content-center">
        <img class="my-2" src="/static/images/illustrations/2.svg" alt="LinkedIn"/>
        <p>{{ $t('onboarding.sync-connections.upload-linkedin.redirected-modal-description') }}</p>
        <template v-if="isOnboarding && canOnlySyncOneNetwork">
          <i18n path="onboarding.sync-connections.upload-linkedin.redirected-modal-onboarding-cta" tag="p">
            <template v-slot:ctas.continue>
              <strong>{{ $t('ctas.continue') }}</strong>
            </template>
          </i18n>
          <b-btn class="m-0" variant="primary" @click="() => $emit('finished-uploading')">{{ $t('ctas.continue') }}</b-btn>
        </template>
        <p v-else-if="isOnboarding">{{ $t('onboarding.sync-connections.upload-linkedin.redirected-modal-cta') }}</p>
    </b-modal>

    <contacts-upload-modal ref="contactsUploadModal" network="LINKEDIN" v-bind="{ rejectedCompanyNames, rejectedDomains }"
      @finished-uploading="a => this.$emit('finished-uploading', a)"/>

  </div>
</template>

<script>
import ContactsUploadModal from '@/modals/contacts-upload';
import VideoPlayer from '@/components/misc/video-player';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    canOnlySyncOneNetwork: {
      type: String,
      default: () => null,
    },
    rejectedCompanyNames: {
      type: Array,
      default: () => [],
    },
    rejectedDomains: {
      type: Array,
      default: () => [],
    },
    redirectUri: {
      type: String,
      default: () => null,
    },
  },
  components: {
    ContactsUploadModal,
    VideoPlayer,
  },
  data: () => ({
    readyToUpload: false,
  }),
  created() {
    const { provider, finish } = this.$route.query;
    this.readyToUpload = provider === 'linkedin' && finish === 'uploading';
  },
  filters: {
    prefix(str, n) {
      return `${n}. ${str}`;
    },
  },
  computed: {
    ...mapRoot([ 'company' ]),
    linkedInUrl: () => 'https://www.linkedin.com/psettings/member-data',
    isOnboarding() {
      const { name } = this.$route;
      return name === 'Onboarding';
    },
    eventID() {
      const { eventID: eventHandle } = this.$route.params || {};
      return eventHandle ? `${eventHandle}`.split('-').pop() : null;
    },
    videoOptions() {
      const options = {
        autoplay: false,
        preload: 'auto',
        controls: true,
        sources: [
          {
            type: 'video/mp4',
            src: 'https://real-links-assets.s3.eu-west-2.amazonaws.com/site-assets/video/linkedinUpload-v3.0.0.mp4',
          }
        ],
      };

      if (this.company && this.company.siteBranding && this.company.siteBranding.bigLogo) {
        options.poster = this.company.siteBranding.bigLogo.fileLocation;
      }

      return options;
    },
  },
  methods: {
    // openLinkedInPopup() {
    //   window.open(this.linkedInUrl, 'popup', `width=${window.outerWidth},height=${window.outerHeight}`);
    // },
    async openLinkedInDelayModal() {
      Promise.resolve(this.$root.captureLog({
        eventName: 'CONTACT_NETWORK_REDIRECTED',
        user: { self: true },
        ...(this.eventID ? { event: { id: this.eventID } } : null),
        dataHash: {
          channel: 'LINKEDIN',
          redirected: this.linkedInUrl,
          return: this.redirectUri,
        },
      }));

      this.$bvModal.show('linkedin-redirected');
    },
    askForFile() {
      const { contactsUploadModal } = this.$refs;
      contactsUploadModal.askForFile();
    },
    // receivedFile(e) {
    //   const { contactsUploadModal } = this.$refs;
    //   contactsUploadModal.receivedFile(e);
    // },
  },
};
</script>

<style lang="scss">
.contacts-upload-linkedin {
  .btn.close-x {
    padding: 0;
    background-color: transparent;
    border: 0;
    -webkit-appearance: none;
    color: #000;
    opacity: 0.5;
    text-shadow: 0 1px 0 #fff;
  }

  .card-group {
    .card-footer {
      background-color: transparent;
      border-top: none;
    }
  }

  .video-thumbnail {
    display: inline-block;
    position: relative;

    .btn {
      display: block;
      position:absolute;
      bottom: 0;
      opacity: 0.8;

      &:hover {
        opacity: 0.5;
      }
    }
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .btn, input {
      cursor: pointer;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/contacts-upload/LinkedInParser.vue